import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addCategory,
  fetchCategoryById,
  updateCategory,
} from "slices/category/thunk";
import { AppDispatch } from "slices/store";
import * as Yup from "yup";
import PageContainer from "components/PageContainer/PageContainer";

const textValidation = Yup.string().required("This field is required");

// Common input style for Form.Control to match Autocomplete styling
const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

const CategoryForm: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();

  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const query = new URLSearchParams(location.search);
  // Use 0-based page index for consistency with Category.tsx
  const currentPage = parseInt(query.get("page") || "0");

  const [initialData, setInitialData] = useState({
    categoryName: "",
    remark: "",
  });
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState<boolean>(false); // fetching category
  const [submitting, setSubmitting] = useState<boolean>(false); // form submitting

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData,
    validationSchema: Yup.object({
      categoryName: textValidation.required("Please enter category name"),
    }),
    onSubmit: async (values) => {
      const payload = {
        category_name: values.categoryName,
        remark: values.remark,
      };

      setSubmitting(true);
      try {
        if (isEdit) {
          await dispatch(updateCategory({ id, payload })).unwrap();
          toast.success("Category updated successfully");
        } else {
          await dispatch(addCategory(payload)).unwrap();
          toast.success("Category added successfully");
        }
        navigate("/category", { state: { clearState: !isEdit } });
      } catch (error: any) {
        console.error("Category form error:", error);
        toast.error(error?.message || "Failed to save category");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchCategoryById(id))
        .unwrap()
        .then((res: any) => {
          setInitialData({
            categoryName: res?.category_name || "",
            remark: res?.remark || "",
          });
        })
        .catch(() => toast.error("Failed to load category details"))
        .finally(() => setIsLoading(false));
    }
  }, [id, isEdit, dispatch]);

  const LoadingCard = () => (
    <div className="text-center py-5 bg-white rounded-3 shadow-sm">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  return (
    <PageContainer>
      <FormHeader
        title={isEdit ? "Edit Category" : "Add Category"}
        firstLabel="Category"
        secondLabel={isEdit ? "Edit Category" : "Add Category"}
        handleArrowClick={() => navigate("/category")}
      />

      {isEdit && isLoading ? (
        <LoadingCard />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <Form.Label>
                  Category Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="categoryName"
                  placeholder="Enter Category Name"
                  value={formik.values.categoryName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.categoryName && formik.touched.categoryName
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.categoryName}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!formik.errors.remark && formik.touched.remark}
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.remark}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton to={-1} disabled={submitting} />

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={submitting} isEdit={isEdit} />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default CategoryForm;
